import { useEffect, useState } from "react";
import { Audit } from "@/lib/api";
import {
  History,
  ScrollText,
  Receipt as ReceiptIcon,
  TrendingUp,
  Banknote,
  Mail,
  MessageSquare,
} from "lucide-react";

const ICON_BY_TYPE = {
  bill: ScrollText,
  receipt: ReceiptIcon,
  income: TrendingUp,
  bank_txn: Banknote,
};
const ACTION_LABEL = {
  create: "Created",
  update: "Updated",
  delete: "Deleted",
  payment: "Payment",
  allocate: "Allocated",
  email: "Emailed",
  text: "Texted",
};
const ACTION_COLOR = {
  create: "bg-green-600",
  update: "bg-blue-600",
  delete: "bg-red-600",
  payment: "bg-green-700",
  allocate: "bg-green-700",
  email: "bg-neutral-700",
  text: "bg-neutral-700",
};

const HistoryPage = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    setLoading(true);
    Audit.list({ limit: 500 })
      .then(setItems)
      .finally(() => setLoading(false));
  }, []);

  const filtered =
    filter === "all" ? items : items.filter((i) => i.entity_type === filter);

  return (
    <div data-testid="history-page" className="fade-rise">
      <div className="swiss-label mb-3 inline-flex items-center gap-2">
        <History className="h-3 w-3" /> Audit log
      </div>
      <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tight leading-tight">
        History
      </h1>
      <p className="text-neutral-600 mt-3 max-w-2xl">
        Every change recorded — when bills were created, paid, edited or
        allocated. Filter by type below.
      </p>

      <div className="flex flex-wrap gap-2 mt-6" data-testid="history-filters">
        {[
          { k: "all", label: "All" },
          { k: "bill", label: "Bills" },
          { k: "receipt", label: "Receipts" },
          { k: "income", label: "Income" },
        ].map((f) => {
          const active = filter === f.k;
          return (
            <button
              key={f.k}
              data-testid={`hist-filter-${f.k}`}
              onClick={() => setFilter(f.k)}
              className={`px-3 py-2 border-2 text-[11px] uppercase tracking-widest font-bold transition-colors ${
                active
                  ? "bg-black text-white border-black"
                  : "bg-white text-black border-neutral-300 hover:border-black"
              }`}
            >
              {f.label}
            </button>
          );
        })}
      </div>

      {loading ? (
        <div className="mt-8 text-sm text-neutral-500">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="mt-10 border-2 border-dashed border-neutral-300 p-10 text-center text-sm text-neutral-500">
          No history yet. Make a change and it'll show up here.
        </div>
      ) : (
        <div className="mt-8 border-2 border-black bg-white">
          <ul data-testid="history-list">
            {filtered.map((i) => {
              const Icon = ICON_BY_TYPE[i.entity_type] || History;
              const actionColor = ACTION_COLOR[i.action] || "bg-neutral-500";
              return (
                <li
                  key={i.id}
                  className="border-t first:border-t-0 border-neutral-200 px-4 py-3 flex items-start gap-4"
                  data-testid={`hist-row-${i.id}`}
                >
                  <div className="flex-none h-10 w-10 border-2 border-black flex items-center justify-center">
                    <Icon className="h-4 w-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span
                        className={`text-[9px] uppercase tracking-widest font-bold text-white px-2 py-0.5 ${actionColor}`}
                      >
                        {ACTION_LABEL[i.action] || i.action}
                      </span>
                      <span className="text-[10px] uppercase tracking-widest text-neutral-500">
                        {i.entity_type}
                      </span>
                    </div>
                    <div className="text-sm mt-1 break-words">{i.summary}</div>
                  </div>
                  <div className="text-[11px] uppercase tracking-widest text-neutral-500 mono whitespace-nowrap">
                    {new Date(i.ts).toLocaleString("en-AU", {
                      day: "numeric",
                      month: "short",
                      hour: "numeric",
                      minute: "2-digit",
                    })}
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
};

export default HistoryPage;
